import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeftRight } from "lucide-react";
import { useAcceptAppTransfer, useAppTransfers, useCancelAppTransfer } from "../hooks/useApps";
import type { AppTransfer } from "../api/apps.api";
import { Alert } from "@/components/ui/Alert";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { formatDate } from "@/lib/formatters";

const secondaryButtonClass =
  "rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium text-muted transition-colors hover:border-border-light hover:text-heading disabled:opacity-50 disabled:cursor-not-allowed";

const primaryButtonClass =
  "rounded-lg bg-indigo px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-indigo-bright disabled:opacity-50 disabled:cursor-not-allowed";

function transferLabel(transfer: AppTransfer): string {
  return transfer.appName ?? transfer.appId;
}

export function PendingTransfersSection() {
  const { t } = useTranslation();
  const transfersQuery = useAppTransfers();
  const acceptTransfer = useAcceptAppTransfer();
  const cancelTransfer = useCancelAppTransfer();
  const [acceptTarget, setAcceptTarget] = useState<AppTransfer | null>(null);
  const [cancelTarget, setCancelTarget] = useState<AppTransfer | null>(null);

  const inbound = transfersQuery.data?.inbound ?? [];
  const outbound = transfersQuery.data?.outbound ?? [];
  const hasAny = inbound.length > 0 || outbound.length > 0;

  if (transfersQuery.isLoading) return null;
  if (transfersQuery.error && !hasAny) {
    return <ApiErrorBanner error={transfersQuery.error} className="mt-8" />;
  }
  if (!hasAny) return null;

  const acceptBusy = acceptTransfer.isPending;
  const cancelBusy = cancelTransfer.isPending;

  return (
    <section className="mt-8 rounded-xl border border-border bg-surface p-5">
      <div className="flex items-center gap-2">
        <ArrowLeftRight size={16} className="text-indigo" />
        <h2 className="text-base font-semibold text-heading">{t("apps.transfers.title")}</h2>
      </div>
      <p className="mt-1 text-xs text-muted">{t("apps.transfers.subtitle")}</p>

      {inbound.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-heading">{t("apps.transfers.inbound")}</h3>
          <ul className="mt-2 divide-y divide-border">
            {inbound.map((transfer) => (
              <li
                key={transfer.id}
                className="flex flex-wrap items-center justify-between gap-3 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-heading">
                    {transferLabel(transfer)}
                  </p>
                  <p className="mt-0.5 text-xs text-muted">
                    {t("apps.transfers.from", { email: transfer.fromEmail ?? "—" })}
                    {" · "}
                    {t("apps.transfers.expires", {
                      date: formatDate(transfer.expiresAt),
                    })}
                  </p>
                </div>
                <button
                  type="button"
                  className={primaryButtonClass}
                  disabled={acceptBusy}
                  onClick={() => setAcceptTarget(transfer)}
                >
                  {acceptBusy && acceptTarget?.id === transfer.id
                    ? t("apps.transfers.accepting")
                    : t("apps.transfers.accept")}
                </button>
              </li>
            ))}
          </ul>
          {acceptTransfer.error && (
            <p role="alert" className="mt-2 text-xs text-danger">
              {acceptTransfer.error.error ?? t("common.error")}
            </p>
          )}
        </div>
      )}

      {outbound.length > 0 && (
        <div className={`mt-4 ${inbound.length > 0 ? "border-t border-border pt-4" : ""}`}>
          <h3 className="text-sm font-semibold text-heading">{t("apps.transfers.outbound")}</h3>
          <ul className="mt-2 divide-y divide-border">
            {outbound.map((transfer) => (
              <li
                key={transfer.id}
                className="flex flex-wrap items-center justify-between gap-3 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-heading">
                    {transferLabel(transfer)}
                  </p>
                  <p className="mt-0.5 text-xs text-muted">
                    {t("apps.transfers.to", { email: transfer.toEmail ?? "—" })}
                    {" · "}
                    {t("apps.transfers.expires", {
                      date: formatDate(transfer.expiresAt),
                    })}
                  </p>
                </div>
                <button
                  type="button"
                  className={secondaryButtonClass}
                  disabled={cancelBusy}
                  onClick={() => setCancelTarget(transfer)}
                >
                  {cancelBusy && cancelTarget?.id === transfer.id
                    ? t("apps.transfers.cancelling")
                    : t("apps.transfers.cancel")}
                </button>
              </li>
            ))}
          </ul>
          {cancelTransfer.error && (
            <p role="alert" className="mt-2 text-xs text-danger">
              {cancelTransfer.error.error ?? t("common.error")}
            </p>
          )}
        </div>
      )}

      <ConfirmDialog
        open={acceptTarget !== null}
        onClose={() => {
          setAcceptTarget(null);
          acceptTransfer.reset();
        }}
        onConfirm={() => {
          if (!acceptTarget) return;
          acceptTransfer.mutate(acceptTarget.id, {
            onSuccess: () => setAcceptTarget(null),
          });
        }}
        title={t("apps.transfers.acceptTitle")}
        description={t("apps.transfers.acceptConfirm", {
          appName: acceptTarget ? transferLabel(acceptTarget) : "",
        })}
        confirmLabel={t("apps.transfers.accept")}
        cancelLabel={t("common.cancel")}
        closeOnConfirm={false}
        isPending={acceptBusy}
        error={acceptTransfer.error?.error}
      />

      <ConfirmDialog
        open={cancelTarget !== null}
        onClose={() => {
          setCancelTarget(null);
          cancelTransfer.reset();
        }}
        onConfirm={() => {
          if (!cancelTarget) return;
          cancelTransfer.mutate(cancelTarget.id, {
            onSuccess: () => setCancelTarget(null),
          });
        }}
        title={t("apps.transfers.cancelTitle")}
        description={t("apps.transfers.cancelConfirm", {
          appName: cancelTarget ? transferLabel(cancelTarget) : "",
        })}
        confirmLabel={t("apps.transfers.cancel")}
        cancelLabel={t("common.cancel")}
        destructive
        closeOnConfirm={false}
        isPending={cancelBusy}
        error={cancelTransfer.error?.error}
      />

      {(inbound.length > 0 || outbound.length > 0) && (
        <Alert variant="info" className="mt-4">
          {t("apps.transfers.collaboratorsLeaveNote")}
        </Alert>
      )}
    </section>
  );
}
